import { uiText } from '../i18n/uiText';
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { templatesApi, auditsApi, getApiErrorMessage } from '../api';
import { ArrowLeft, Plus, Trash2, GripVertical, ChevronDown, ChevronUp, Upload, Download, CheckCircle2 } from 'lucide-react';
import { useConfirm } from '../store/ConfirmContext';
import ActionMenu from '../components/common/ActionMenu';
import PageIntro from '../components/ui/PageIntro';
import { useAuth } from '../store/AuthContext';
import useDraftBuffer from '../hooks/useDraftBuffer';
import FormDraftNotice from '../components/common/FormDraftNotice';
import ExpectedAnswerModal from '../components/audits/ExpectedAnswerModal';

const Q_TYPES = [
  { value: 'text', label: uiText("Текст") },
  { value: 'yesno', label: uiText("Да / Нет") },
  { value: 'number', label: uiText("Число") },
  { value: 'select', label: uiText("Список вариантов") },
  { value: 'photo', label: uiText("Файл / фото") },
  { value: 'signature', label: uiText("Подпись") },
];

const ANSWER_SCOPES = [
  { value: 'auditor', label: uiText("Заполняет аудитор") },
  { value: 'customer', label: uiText("Заполняет аудируемый") },
  { value: 'both', label: uiText("Могут оба") },
];

const newQuestion = () => ({
  _id: Date.now() + Math.random(),
  text: '',
  question_type: 'text',
  options: null,
  required: false,
  answer_scope: 'auditor',
  allow_comment: false,
  allow_photo: false,
  order: 0,
});
const newSection = () => ({ _id: Date.now() + Math.random(), title: uiText("Новый раздел"), questions: [newQuestion()], order: 0 });

const normalizeOrder = (value, fallback = 0) => {
  if (value === null || value === undefined || value === '') return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export default function TemplateEditorPage() {
  const { id } = useParams();
  return <TemplateEditor key={id || 'new'} />;
}

function TemplateEditor() {
  const confirm = useConfirm();
  const { id } = useParams();
  const location = useLocation();
  const isNew = !id || id === 'new' || location.pathname.endsWith('/templates/new');
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const { user } = useAuth();
  const buffer = useDraftBuffer(user?.id, isNew ? 'new' : id, 'audit-template');
  const initialSections = useRef([newSection()]);
  const form = buffer.values.form || { name: '', description: '', audit_type: '', client: '', language: 'RU' };
  const sections = buffer.values.sections || initialSections.current;
  const setForm = update => { if (!saving && !buffer.recovery && buffer.active) buffer.edit('form', typeof update === 'function' ? update(buffer.values.form || form) : update); };
  const setSections = update => { if (!saving && !buffer.recovery && buffer.active) buffer.edit('sections', typeof update === 'function' ? update(buffer.values.sections || sections) : update); };
  const [taxonomy, setTaxonomy] = useState({ audit_kinds: [] });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [importSummary, setImportSummary] = useState(null);
  const [templateMeta, setTemplateMeta] = useState(null);
  const [collapsed, setCollapsed] = useState({});
  const [activeTemplateStep, setActiveTemplateStep] = useState('basic');
  const [guidanceTarget, setGuidanceTarget] = useState(null);
  const [guidanceProgress, setGuidanceProgress] = useState(null);
  const [guidanceDocument, setGuidanceDocument] = useState(null);
  const guidanceAbort = useRef(null);
  useEffect(() => () => guidanceAbort.current?.abort(), []);

  const generateGuidance = async documentId => {
    if (guidanceAbort.current || saving || buffer.recovery) return;
    const controller = new AbortController();
    guidanceAbort.current = controller;
    const targets = sections.flatMap(section => section.questions.map(question => ({ sectionId: section._id, question })))
      .filter(({ question }) => question.text.trim() && (guidanceTarget === 'all' ? !question.expected_answer?.trim() : question._id === guidanceTarget?.questionId));
    setGuidanceDocument(documentId);
    setGuidanceTarget(null);
    setGuidanceProgress({ done: 0, total: targets.length });
    setError('');
    try {
      for (const [index, target] of targets.entries()) {
        const response = await templatesApi.generateExpectedAnswer({ question: target.question.text, document_id: documentId }, { signal: controller.signal });
        if (!buffer.active || controller.signal.aborted) break;
        // Never overwrite text changed while the model was preparing a response.
        const current = buffer.values.sections || sections;
        buffer.edit('sections', current.map(section => section._id !== target.sectionId ? section : { ...section,
          questions: section.questions.map(question => question._id === target.question._id && question.text === target.question.text && question.expected_answer === target.question.expected_answer
            ? { ...question, expected_answer: response.data.expected_answer, expected_answer_source_id: documentId } : question) }));
        setGuidanceProgress({ done: index + 1, total: targets.length });
      }
    } catch (error) {
      if (!controller.signal.aborted && buffer.active) setError(uiText("{v0} Уже подготовленные ответы оставлены в черновике.", {v0: (getApiErrorMessage(error))}));
    } finally {
      guidanceAbort.current = null;
      if (buffer.active) setGuidanceProgress(null);
    }
  };

  useEffect(() => {
    auditsApi.taxonomy()
      .then(res => setTaxonomy(res.data || { audit_kinds: [] }))
      .catch(() => setTaxonomy({ audit_kinds: [] }));
  }, []);

  useEffect(() => {
    if (!isNew) {
      setLoading(true);
      setImportSummary(null);
      templatesApi.get(id).then(res => {
        if (!buffer.active) return;
        const t = res.data;
        setTemplateMeta({ id: t.id, status: t.status, version: t.version });
        buffer.hydrate({ form: { name: t.name || '', description: t.description || '', audit_type: t.audit_type || '', client: t.client || '', language: t.language || 'RU' },
          sections: (t.sections || []).map(s => ({
          ...s,
          _id: s.id,
          questions: (s.questions || []).map(q => ({
            ...q,
            _id: q.id,
            required: !!q.required,
            answer_scope: q.answer_scope || 'auditor',
            allow_comment: !!q.allow_comment,
            allow_photo: !!q.allow_photo,
          }))
        })) });
        setLoading(false);
      }).catch(e => { if (buffer.active) { setError(getApiErrorMessage(e) || uiText("Не удалось загрузить шаблон")); setLoading(false); } });
    }
  }, [id, isNew]);

  const auditKindOptions = taxonomy.audit_kinds || [];

  const addSection = () => setSections(prev => [...prev, newSection()]);
  const removeSection = sid => setSections(prev => prev.filter(s => s._id !== sid));
  const updateSection = (sid, title) => setSections(prev => prev.map(s => s._id === sid ? { ...s, title } : s));
  const addQuestion = sid => setSections(prev => prev.map(s => s._id === sid ? { ...s, questions: [...s.questions, newQuestion()] } : s));
  const removeQuestion = (sid, qid) => setSections(prev => prev.map(s => s._id === sid ? { ...s, questions: s.questions.filter(q => q._id !== qid) } : s));
  const updateQuestion = (sid, qid, field, value) => setSections(prev => prev.map(s => s._id === sid ? { ...s, questions: s.questions.map(q => q._id === qid ? { ...q, [field]: value } : q) } : s));

  const handleDownloadTemplate = async () => {
    try {
      const res = await templatesApi.downloadExcelTemplate();
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'aac_template_questions.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(uiText("Не удалось скачать шаблон Excel"));
    }
  };

  const handleImportExcel = async (file) => {
    if (!file || saving || guidanceAbort.current || buffer.recovery) return;
    try {
      const res = await templatesApi.importExcel(file);
      const importedSections = (res.data.sections || []).map((s, si) => ({
        _id: Date.now() + Math.random() + si,
        title: s.title,
        order: s.order ?? si,
        questions: (s.questions || []).map((q, qi) => ({
          _id: Date.now() + Math.random() + si + qi,
          text: q.text || '',
          question_type: q.question_type || 'text',
          options: q.options || null,
          required: !!q.required,
          answer_scope: q.answer_scope || 'auditor',
          allow_comment: !!q.allow_comment,
          allow_photo: !!q.allow_photo,
          order: normalizeOrder(q.order, qi),
          expected_answer: q.expected_answer || '',
          expected_answer_source_id: q.expected_answer_source_id || null,
        })),
      }));
      if (!importedSections.length) {
        setError(uiText("В Excel не найдено вопросов для импорта"));
        return;
      }
      const shouldReplace = await confirm(uiText("Заменить текущие разделы и вопросы содержимым из Excel?"), { title: uiText("Импорт из Excel"), confirmLabel: uiText("Заменить") });
      if (!shouldReplace) return;
      setSections(importedSections);
      const questionCount = importedSections.reduce((sum, section) => sum + section.questions.length, 0);
      const requiredCount = importedSections.reduce(
        (sum, section) => sum + section.questions.filter(question => question.required).length,
        0,
      );
      setImportSummary(res.data.summary || {
        sections: importedSections.length,
        questions: questionCount,
        required: requiredCount,
      });
      setError('');
    } catch (e) {
      // FIX: e.response?.data?.detail can be a Pydantic array → React render crash
      setError(getApiErrorMessage(e) || uiText("Ошибка импорта Excel"));
    }
  };

  const handleSave = async (andPublish = false) => {
    if (saving || guidanceAbort.current || loading || buffer.recovery || (!isNew && !templateMeta)) return;
    if (!form.name.trim()) { setError(uiText("Введите название шаблона")); return; }
    if (!sections.length) { setError(uiText("Добавьте хотя бы один раздел")); return; }
    for (const section of sections) {
      if (!section.title?.trim()) { setError(uiText("У каждого раздела должно быть название")); return; }
      if (!section.questions?.length) { setError(uiText("В разделе «{v0}» нет вопросов", {v0: (section.title || 'Без названия')})); return; }
      for (const question of section.questions) {
        if (!question.text?.trim()) { setError(uiText("В разделе «{v0}» есть вопрос без текста", {v0: (section.title || 'Без названия')})); return; }
        if (question.question_type === 'select') {
          const options = typeof question.options === 'string'
            ? question.options.split(',').map(x => x.trim()).filter(Boolean)
            : Array.isArray(question.options) ? question.options.filter(Boolean) : [];
          if (!options.length) { setError(uiText("Для вопроса «{v0}» заполните варианты ответа через запятую", {v0: (question.text)})); return; }
        }
      }
    }
    const snapshot = buffer.snapshot();
    setSaving(true); setError('');
    try {
      const payload = {
        ...form,
        status: andPublish ? 'published' : 'draft',
        sections: sections.map((s, i) => ({
          title: s.title,
          order: normalizeOrder(s.order, i),
          questions: s.questions.map((q, j) => ({
            text: q.text,
            question_type: q.question_type,
            options: q.question_type === 'select'
              ? (typeof q.options === 'string'
                  ? q.options.split(',').map(x => x.trim()).filter(Boolean)
                  : Array.isArray(q.options) ? q.options : [])
              : null,
            required: q.required,
            answer_scope: q.answer_scope || 'auditor',
            allow_comment: !!q.allow_comment,
            allow_photo: !!q.allow_photo,
            expected_answer: q.expected_answer || null,
            expected_answer_source_id: q.expected_answer_source_id || null,
            order: normalizeOrder(q.order, j),
          }))
        }))
      };
      let response;
      if (isNew) {
        try {
          response = await templatesApi.create(payload);
        } catch (err) {
          const msg = getApiErrorMessage(err) || '';
          if (msg.toLowerCase().includes('valid integer') || msg.toLowerCase().includes('integer')) {
            response = await templatesApi.saveNewCompat(payload, andPublish);
          } else {
            throw err;
          }
        }
      } else {
        response = await templatesApi.update(id, payload);
      }
      const savedId = response?.data?.id;
      buffer.acknowledge(snapshot, { committed: true });
      if (!buffer.active || buffer.count) return;
      if (!isNew && savedId && String(savedId) !== String(id)) {
        navigate(`/templates/${savedId}`, { replace: true, state: { versionCreated: true } });
      } else {
        navigate('/templates');
      }
    } catch (e) {
      // FIX: detail may be array/object — use getApiErrorMessage to get a safe string
      setError(getApiErrorMessage(e) || uiText("Ошибка сохранения"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  const totalQuestions = sections.reduce((sum, s) => sum + s.questions.length, 0);
  const templateSteps = [
    { key: 'basic', label: uiText("Основные параметры"), complete: Boolean(form.name) },
    {
      key: 'sections',
      label: uiText("Разделы и вопросы"),
      complete: Boolean(sections.length && sections.every((section) => section.title && section.questions?.length && section.questions.every((question) => question.text))),
    },
    { key: 'review', label: uiText("Проверка и публикация"), complete: Boolean(form.name && totalQuestions) },
  ];
  const goToTemplateStep = (key) => {
    setActiveTemplateStep(key);
    document.getElementById(`template-step-${key}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="workspace-page template-editor-page">
      <PageIntro
        icon={CheckCircle2}
        title={isNew ? uiText("Новый шаблон чек-листа") : uiText("Редактирование шаблона чек-листа")}
        description={uiText("Настройте разделы и вопросы, затем сохраните черновик или опубликуйте готовую версию.")}
        aside={(
          <div className="workspace-side-actions">
            <button type="button" className="btn btn-outline btn-sm" onClick={() => navigate('/templates')}><ArrowLeft size={14} />{uiText(" Назад")}</button>
            <input ref={fileRef} type="file" accept=".xlsx" style={{ display: 'none' }} onChange={e => { const file = e.target.files?.[0]; handleImportExcel(file); e.target.value = ''; }} />
            <ActionMenu
              label={uiText("Другие действия с шаблоном")}
              actions={[
                { key: 'import', label: uiText("Импортировать из Excel"), icon: Upload, onClick: () => fileRef.current?.click() },
                { key: 'download', label: uiText("Скачать шаблон Excel"), icon: Download, onClick: handleDownloadTemplate },
                { key: 'draft', label: uiText("Сохранить черновик"), onClick: () => handleSave(false), disabled: saving },
              ]}
            />
            <button type="button" className="btn btn-primary" onClick={() => handleSave(true)} disabled={saving || !!guidanceProgress}>{saving ? uiText("Сохранение...") : uiText("Сохранить и опубликовать")}</button>
          </div>
        )}
      />

      {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}
      <FormDraftNotice buffer={buffer} label={uiText("Шаблон чек-листа")} disabled={saving || loading} />
      <div className="card" style={{ marginBottom: 16 }}><div className="card-body" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 260px' }}><strong>{uiText("Подготовка ожидаемых ответов")}</strong><p className="text-muted" style={{ margin: '6px 0 0' }}>{uiText("ИИ заполнит пустые подсказки по выбранному стандарту. Проверьте их перед публикацией. Заполненные вручную ответы сохранятся.")}</p></div>
        {guidanceProgress ? <><span role="status">{uiText("Подготовлено ")}{guidanceProgress.done}{uiText(" из ")}{guidanceProgress.total}</span><button className="btn btn-outline" type="button" onClick={() => guidanceAbort.current?.abort()}>{uiText("Остановить")}</button></>
          : <button className="btn btn-outline" type="button" disabled={saving || !!buffer.recovery} onClick={() => setGuidanceTarget('all')}>{uiText("Заполнить пустые ответы по стандарту")}</button>}
      </div></div>
      {importSummary && (
        <div className="alert alert-success template-import-summary" role="status">{uiText("Импортировано: ")}{importSummary.sections}{uiText(" разделов, ")}{importSummary.questions}{uiText(" вопросов. Обязательных вопросов: ")}{importSummary.required}.
        </div>
      )}
      {!isNew && templateMeta?.status === 'published' && (
        <div className="context-help" style={{ marginBottom: 16 }}>{uiText("Вы редактируете опубликованный шаблон ")}{templateMeta.version}{uiText(". При сохранении система создаст новую версию и откроет именно её; исходная версия останется в истории аудитов.")}</div>
      )}

      <div className="form-stepper" aria-label={uiText("Этапы настройки шаблона")}>
        {templateSteps.map((step, index) => (
          <button
            key={step.key}
            type="button"
            className={`form-stepper-button ${activeTemplateStep === step.key ? 'is-active' : ''} ${step.complete ? 'is-complete' : ''}`}
            onClick={() => goToTemplateStep(step.key)}
            aria-current={activeTemplateStep === step.key ? 'step' : undefined}
          >
            <span className="form-stepper-number">{step.complete ? <CheckCircle2 size={13} /> : index + 1}</span>
            {step.label}
          </button>
        ))}
      </div>

      <div className="card" id="template-step-basic" style={{ marginBottom: '20px', scrollMarginTop: 76 }}>
        <div className="card-header"><div className="card-title">{uiText("Основные параметры")}</div></div>
        <div className="card-body">
          <div className="form-row">
            <div className="form-group"><label className="form-label required">{uiText("Название шаблона")}</label><input className="form-control" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} /></div>
            <div className="form-group"><label className="form-label">{uiText("Клиент")}</label><input className="form-control" value={form.client} onChange={e => setForm(p => ({ ...p, client: e.target.value }))} /></div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">{uiText("Тип аудита")}</label>
              <select className="form-control" value={form.audit_type} onChange={e => setForm(p => ({ ...p, audit_type: e.target.value }))}>
                <option value="">{uiText("Не задан")}</option>
                {auditKindOptions.map(k => <option key={k.code} value={k.code}>{k.label}</option>)}
              </select>
            </div>
            <div className="form-group"><label className="form-label">{uiText("Язык")}</label><select className="form-control" value={form.language} onChange={e => setForm(p => ({ ...p, language: e.target.value }))}><option value="RU">RU</option><option value="EN">EN</option></select></div>
          </div>
          <div className="form-group"><label className="form-label">{uiText("Описание")}</label><textarea className="form-control" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={2} /></div>
        </div>
      </div>

      <div id="template-step-sections" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', scrollMarginTop: 76 }}>
        <h2 style={{ fontSize: '16px', fontWeight: '600' }}>{uiText("Разделы чек-листа (")}{sections.length}{uiText(" разд. · ")}{totalQuestions}{uiText(" вопр.)")}</h2>
        <button type="button" className="btn btn-outline btn-sm" onClick={addSection}><Plus size={14} />{uiText(" Добавить раздел")}</button>
      </div>

      {sections.map(section => (
        <div key={section._id} className="section-block">
          <div className="section-header">
            <GripVertical size={16} color="#aaa" />
            <input className="section-title-input" value={section.title} onChange={e => updateSection(section._id, e.target.value)} />
            <span style={{ fontSize: '12px', color: '#888' }}>{section.questions.length}{uiText(" вопр.")}</span>
            <button type="button" className="ap-button ap-button--secondary ap-control-icon" aria-label={collapsed[section._id] ? uiText("Развернуть раздел") : uiText("Свернуть раздел")} aria-expanded={!collapsed[section._id]} onClick={() => setCollapsed(p => ({ ...p, [section._id]: !p[section._id] }))}>{collapsed[section._id] ? <ChevronDown size={16} /> : <ChevronUp size={16} />}</button>
            {sections.length > 1 && <button type="button" className="ap-button ap-button--secondary ap-control-icon" aria-label={uiText("Удалить раздел")} onClick={() => removeSection(section._id)}><Trash2 size={16} /></button>}
          </div>

          {!collapsed[section._id] && (<>
            {section.questions.map((q, qi) => (
              <div key={q._id} className="question-row" style={{ alignItems: 'flex-start', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', gap: 8, width: '100%', alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ color: 'var(--premium-text-secondary)', fontSize: '12px', minWidth: '20px' }}>{qi + 1}.</span>
                  <input className="question-text-input" placeholder={uiText("Текст вопроса...")} value={q.text} onChange={e => updateQuestion(section._id, q._id, 'text', e.target.value)} />
                  <select className="form-control" style={{ maxWidth: 180 }} value={q.question_type} onChange={e => updateQuestion(section._id, q._id, 'question_type', e.target.value)}>{Q_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}</select>
                  <select className="form-control" style={{ maxWidth: 220 }} value={q.answer_scope || 'auditor'} onChange={e => updateQuestion(section._id, q._id, 'answer_scope', e.target.value)}>{ANSWER_SCOPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}</select>
                  <label className={`template-question-required ${q.required ? 'is-checked' : ''}`}>
                    <input
                      type="checkbox"
                      checked={!!q.required}
                      onChange={e => updateQuestion(section._id, q._id, 'required', e.target.checked)}
                      aria-label={uiText("Обязательный вопрос {v0}", {v0: (qi + 1)})}
                    />
                    <span>{q.required ? uiText("Обязательный") : uiText("Необязательный")}</span>
                  </label>
                  {section.questions.length > 1 && <button type="button" className="ap-button ap-button--secondary ap-control-icon" aria-label={uiText("Удалить вопрос {v0}", {v0: (qi + 1)})} onClick={() => removeQuestion(section._id, q._id)}><Trash2 size={16} /></button>}
                </div>
                <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', paddingLeft: 28 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                    <input type="checkbox" checked={!!q.allow_comment} onChange={e => updateQuestion(section._id, q._id, 'allow_comment', e.target.checked)} />{uiText("Поле для фиксации свидетельств")}</label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                    <input type="checkbox" checked={!!q.allow_photo} onChange={e => updateQuestion(section._id, q._id, 'allow_photo', e.target.checked)} />{uiText("Возможность приложить файл")}</label>
                </div>
                {q.question_type === 'select' && (
                  <input className="form-control" style={{ width: '100%' }} placeholder={uiText("Варианты через запятую: Да, Нет, N/A")} value={Array.isArray(q.options) ? q.options.join(', ') : (q.options || '')} onChange={e => updateQuestion(section._id, q._id, 'options', e.target.value)} />
                )}
                <div className="form-group" style={{ marginTop: 12 }}>
                  <label className="form-label" htmlFor={`guidance-${q._id}`}>{uiText("Ожидаемый ответ — подсказка аудитору")}</label>
                  <textarea id={`guidance-${q._id}`} className="ap-control" rows={2} value={q.expected_answer || ''} placeholder={uiText("Критерии соответствия и ссылка на пункт стандарта")}
                    onChange={event => updateQuestion(section._id, q._id, 'expected_answer', event.target.value)} />
                  <button className="btn btn-outline btn-sm" style={{ marginTop: 8 }} type="button" disabled={!q.text.trim() || saving || !!guidanceProgress || !!buffer.recovery}
                    onClick={() => setGuidanceTarget({ questionId: q._id, text: q.text })}>{uiText("Подготовить ответ по стандарту")}</button>
                </div>
              </div>
            ))}
            <div style={{ padding: '8px 16px', borderTop: '1px solid #f5f5f5' }}><button type="button" className="btn btn-ghost btn-sm" onClick={() => addQuestion(section._id)}><Plus size={13} />{uiText(" Добавить вопрос")}</button></div>
          </>)}
        </div>
      ))}

      <div id="template-step-review" className="workflow-focus-card" style={{ scrollMarginTop: 76 }}>
        <div className="workflow-focus-main">
          <div className="workflow-focus-eyebrow">{uiText("Проверка перед публикацией")}</div>
          <div className="workflow-focus-title">{form.name || uiText("Шаблон без названия")}</div>
          <div className="workspace-muted">{uiText("Разделов: ")}{sections.length}{uiText(" · вопросов: ")}{totalQuestions}{uiText(" · язык: ")}{form.language}
          </div>
          <div className="context-help">{uiText("После публикации шаблон станет доступен при создании аудита. Черновик можно сохранить без публикации.")}</div>
        </div>
        <div className="workspace-side-actions">
          <button type="button" className="btn btn-outline" onClick={() => handleSave(false)} disabled={saving || !!guidanceProgress}>{uiText("Сохранить черновик")}</button>
          <button type="button" className="btn btn-primary" onClick={() => handleSave(true)} disabled={saving || !!guidanceProgress}>{uiText("Сохранить и опубликовать")}</button>
        </div>
      </div>
      {guidanceTarget && <ExpectedAnswerModal question={{ text: guidanceTarget === 'all' ? uiText("Все незаполненные ожидаемые ответы чек-листа") : guidanceTarget.text }}
        defaultDocumentId={guidanceDocument} generating={false} onClose={() => setGuidanceTarget(null)} onGenerate={generateGuidance} />}
    </div>
  );
}
